// ─── NHL DATA TRANSFORMERS — pure functions, no React dependencies ────────────
// Raw NHL API responses → app-ready shapes

import { findPlayerMatch } from "./betting";
import * as Sentry from "./errorReporting";

// ─── LOGO HELPERS ─────────────────────────────────────────────────────────────
export const LOGO_IDS = {ANA:25,BOS:1,BUF:2,CGY:3,CAR:26,CHI:4,COL:21,CBJ:29,DAL:6,DET:7,EDM:9,FLA:13,LAK:8,MIN:30,MTL:10,NSH:18,NJD:16,NYI:11,NYR:12,OTT:17,PHI:15,PIT:14,SEA:55,SJS:28,STL:19,TBL:14,TOR:20,VAN:23,VGK:54,WSH:24,WPG:52,UTA:56};
export const logo = a => LOGO_IDS[a] ? `https://a.espncdn.com/i/teamlogos/nhl/500/scoreboard/${LOGO_IDS[a]}.png` : null;

// Normalize NHL API game → app game shape
export function normalizeNHLGame(game) {
  const h = game.homeTeam;
  const a = game.awayTeam;
  const state = game.gameState; // "FUT","PRE","LIVE","CRIT","FINAL","OFF"
  const period = game.period || 0;
  const clock = game.clock?.timeRemaining || "";
  const isLive = state==="LIVE"||state==="CRIT";
  const isFinal = state==="FINAL"||state==="OFF";
  // If API lags and still reports PRE but period > 0, the game has actually started
  const isActuallyLive = isLive || (!isFinal && period > 0);

  // Convert UTC start time to local time string
  let startTime = "";
  if (game.startTimeUTC) {
    try {
      startTime = new Date(game.startTimeUTC).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"});
    } catch (err) { Sentry.captureException(err, { extra: { operation: 'normalizeNHLGame-timeFormat' } }); }
  }

  return {
    id: String(game.id),
    source: "nhl",
    status: isFinal?"FINAL":isActuallyLive?"LIVE":"UPCOMING",
    // INT = live but between periods (period resets to 0); PRE = not yet started
    quarter: isActuallyLive && period===0 ? "INT" : period===0 ? "PRE" : period>3 ? "OT" : `P${period}`,
    clock: isLive ? clock : "",
    startTime,
    home: {
      team: h.abbrev,
      logo: h.logo || logo(h.abbrev),
      score: h.score || 0,
    },
    away: {
      team: a.abbrev,
      logo: a.logo || logo(a.abbrev),
      score: a.score || 0,
    },
  };
}

// Coerce a raw feed value into a finite number or null. Guards the `numeric`
// DB columns (faceoff_win_pct, save_pct): a malformed string must resolve to
// null, never a NaN/garbage value that would fail the game's atomic upsert.
// null/undefined pass through as null (absent, not zero).
const numOrNull = (v) => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Coerce a team abbrev from adapter output into a short string or null. Guards
// the text `team` column: a non-string (object/array) would fail the game's
// atomic upsert, and an over-long value has no place in an abbrev column. Real
// NHL/MLB abbrevs are 2-3 chars; cap at 4 for safety.
const abbrevOrNull = (v) => (typeof v === 'string' && v.length >= 1 && v.length <= 4 ? v : null);

// Extract all skaters and goalies from a boxscore response into a flat lookup array.
// Every row (skater or goalie) carries the same set of keys for a uniform shape.
// Rules: legacy stat keys (goals/assists/points/sog) stay numeric (0 default) on
// BOTH branches for getPlayerStat/live_stats compatibility; `pim` is a shared stat
// captured as a real value on both branches; every other field holds its real value
// for the applicable position and null when position-inapplicable (an analytics
// N/A — a goalie's `blocks` or a skater's `saves` must not read as 0).
export function parseBoxscorePlayers(data) {
  const players = [];
  ["awayTeam","homeTeam"].forEach(side => {
    // Team abbrev from the boxscore's top-level team block (data.awayTeam.abbrev /
    // data.homeTeam.abbrev), distinct from the playerByGameStats[side] player groups.
    const teamAbbrev = abbrevOrNull(data?.[side]?.abbrev);
    ["forwards","defense"].forEach(group => {
      (data?.playerByGameStats?.[side]?.[group] || []).forEach(p => {
        players.push({
          // ── legacy keys — byte-identical, do not change ──────────────────
          id: typeof p.playerId === "number" ? p.playerId : null,
          name: p.name?.default || "",
          team: teamAbbrev,
          goals: p.goals || 0,
          assists: p.assists || 0,
          points: (p.goals||0) + (p.assists||0),
          sog: p.sog || 0,
          // ── new skater fields ─────────────────────────────────────────────
          blocks: p.blockedShots || 0,
          hits: p.hits || 0,
          pim: p.pim || 0,
          plusMinus: p.plusMinus || 0,
          powerplayGoals: p.powerPlayGoals || 0,
          shorthandedPoints: p.shorthandedPoints ?? null,
          faceoffWinPct: numOrNull(p.faceoffWinningPctg),
          toi: p.toi ?? null,
          shifts: p.shifts || 0,
          giveaways: p.giveaways || 0,
          takeaways: p.takeaways || 0,
          // ── goalie-only fields — position-inapplicable to skaters → null ──
          saves: null,
          shotsAgainst: null,
          goalsAgainst: null,
          savePct: null,
          goalieToi: null,
        });
      });
    });
    (data?.playerByGameStats?.[side]?.goalies || []).forEach(p => {
      players.push({
        // ── legacy keys — real values (goalies can record goals/assists, rare;
        //    usually 0). Kept numeric for shape uniformity + live_stats parity ──
        id: typeof p.playerId === "number" ? p.playerId : null,
        name: p.name?.default || "",
        team: teamAbbrev,
        goals: p.goals || 0,
        assists: p.assists || 0,
        points: (p.goals||0) + (p.assists||0),
        sog: p.sog || 0,
        // ── skater-only fields — position-inapplicable to goalies → null ──
        blocks: null,
        hits: null,
        plusMinus: null,
        powerplayGoals: null,
        shorthandedPoints: null,
        faceoffWinPct: null,
        toi: null,
        shifts: null,
        giveaways: null,
        takeaways: null,
        // ── shared field — captured as a real value on both branches ──────
        pim: p.pim || 0,
        // ── goalie fields ──────────────────────────────────────────────────
        saves: p.saves || 0,
        shotsAgainst: p.shotsAgainst || 0,
        goalsAgainst: p.goalsAgainst || 0,
        savePct: numOrNull(p.savePctg),
        goalieToi: p.toi ?? null,
      });
    });
  });
  return players;
}

// Extract which team scored the first goal of the game from a gamecenter
// payload that carries summary.scoring (/landing; the /boxscore has none).
// Returns the team abbreviation (e.g. "TOR"), or null when no goal is known.
// #1119 D2/D6: walks the periods in order -- regulation and overtime goals
// count; a shootout (periodType 'SO') goal never does, so a 0-0 game decided
// in a shootout returns null. A period with goals but a missing or unknown
// periodType fails closed to null, as does a malformed first goal (never
// skipping ahead to a later goal).
export function parseFirstTeamToScore(data) {
  const periods = data?.summary?.scoring;
  if (!Array.isArray(periods)) return null;
  for (const period of periods) {
    const goals = period?.goals;
    if (!Array.isArray(goals) || goals.length === 0) continue;
    // Allow-list: only REG and OT goals count. An SO period (always the last)
    // or a missing/unknown periodType ends the walk with null.
    const type = period?.periodDescriptor?.periodType;
    if (type !== 'REG' && type !== 'OT') return null;
    const team = goals[0]?.teamAbbrev?.default;
    return typeof team === 'string' && team.trim() ? team.trim() : null;
  }
  return null;
}

// Look up a player's live stats by full name (or numeric ID if available)
export function getPlayerStat(players, fullName, statType, legPlayerId) {
  const match = findPlayerMatch(players, fullName, legPlayerId);
  if (!match) return null;
  switch(statType) {
    case "sog":
    case "nhl:shots_on_goal":
      return match.sog;
    case "goals":
    case "nhl:goals":
    case "anytime_goal":
      return match.goals;
    case "assists":
    case "nhl:assists":
    case "anytime_assist":
    case "nhl:anytime_assist":
      return match.assists;
    case "points":
    case "nhl:points":
    case "anytime_point":
      return match.points;
    default:               return null;
  }
}

